import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { splitSignature } from '@ethersproject/bytes'
import { Contract } from '@ethersproject/contracts'
import { TransactionResponse } from '@ethersproject/providers'
import { useRouter } from 'next/router'
import useToast from 'hooks/useToast'
import { Currency, currencyEquals, ETHER, Percent, WNATIVE } from '@magikswap/sdk'
import {
  Button,
  Text,
  AddIcon,
  ArrowDownIcon,
  Box,
  Flex,
  useModal,
  TooltipText,
  useTooltip,
  useMatchBreakpointsContext,
} from '@pancakeswap/uikit'
import Slider from 'components/Slider'
import { V2_ROUTER_ADDRESSES } from '@baseswapfi/sdk-core'
import { BigNumber } from '@ethersproject/bignumber'
import { callWithEstimateGas } from 'utils/calls'
import GlobalSettings from 'components/Menu/GlobalSettings'
import { SettingsMode } from 'components/Menu/GlobalSettings/types'
import { useTranslation } from '@pancakeswap/localization'
import { useLPApr } from 'state/swap/hooks'
import { transactionErrorToUserReadableMessage } from 'utils/transactionErrorToUserReadableMessage'
import { AutoColumn, ColumnCenter } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { RowBetween } from 'components/Layout/Row'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { LightGreyCard } from 'components/Card'
import { CurrencyLogo } from 'components/Logo'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useCurrency } from 'hooks/Tokens'
import { usePairContract, useZapContract } from 'hooks/useContract'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import { useTransactionAdder } from 'state/transactions/hooks'
import StyledInternalLink from 'components/Links'
import { calculateGasMargin } from 'utils'
import { getRouterContract, calculateSlippageAmount } from 'utils/exchange'
import { currencyId } from 'utils/currencyId'
import useDebouncedChangeHandler from 'hooks/useDebouncedChangeHandler'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import { useApproveCallback, ApprovalState } from 'hooks/useApproveCallback'
import { useBurnActionHandlers, useDerivedBurnInfo, useBurnState } from 'state/burn/hooks'
import { Field } from 'state/burn/actions'
import { useGasPrice, useUserSlippageTolerance } from 'state/user/hooks'
import ConfirmLiquidityModal from 'views/Swap/components/ConfirmRemoveLiquidityModal'
import { logError } from 'utils/sentry'
import { formatAmount } from 'utils/formatInfoNumbers'
import RingLoader from 'react-spinners/RingLoader'
import { BorderCard } from '../styled'

export default function RemoveLiquidity({
  currencyA,
  currencyB,
  totalUSDValue,
  tokenADeposited,
  tokenBDeposited,
  userPoolBalance,
  poolTokenPercentage,
}) {
  const router = useRouter()
  const [currencyIdA, currencyIdB] = router.query.currency || []
  const { account, chainId, library } = useActiveWeb3React()
  const { toastError } = useToast()

  const [tokenA, tokenB] = useMemo(
    () => [wrappedCurrency(currencyA, chainId), wrappedCurrency(currencyB, chainId)],
    [currencyA, currencyB, chainId],
  )
  const { t } = useTranslation()
  const gasPrice = useGasPrice()
  // burn state
  const { independentField, typedValue } = useBurnState()
  const [removalCheckedA, setRemovalCheckedA] = useState(true)
  const [removalCheckedB, setRemovalCheckedB] = useState(true)
  const { pair, parsedAmounts, error } = useDerivedBurnInfo(
    currencyA ?? undefined,
    currencyB ?? undefined,
    removalCheckedA,
    removalCheckedB,
  )
  const { onUserInput: _onUserInput } = useBurnActionHandlers()
  const isValid = !error
  // modal and loading
  const [{ attemptingTxn, liquidityErrorMessage, txHash }, setLiquidityState] = useState<{
    attemptingTxn: boolean
    liquidityErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    attemptingTxn: false,
    liquidityErrorMessage: undefined,
    txHash: undefined,
  })

  // txn values
  const deadline = useTransactionDeadline()
  const [allowedSlippage] = useUserSlippageTolerance()
  const formattedAmounts = {
    [Field.LIQUIDITY_PERCENT]: parsedAmounts[Field.LIQUIDITY_PERCENT].equalTo('0')
      ? '0'
      : parsedAmounts[Field.LIQUIDITY_PERCENT].lessThan(new Percent('1', '100'))
      ? '<1'
      : parsedAmounts[Field.LIQUIDITY_PERCENT].toFixed(0),
    [Field.LIQUIDITY]:
      independentField === Field.LIQUIDITY ? typedValue : parsedAmounts[Field.LIQUIDITY]?.toSignificant(6) ?? '',
    [Field.CURRENCY_A]:
      independentField === Field.CURRENCY_A ? typedValue : parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) ?? '',
    [Field.CURRENCY_B]:
      independentField === Field.CURRENCY_B ? typedValue : parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) ?? '',
  }

  const atMaxAmount = parsedAmounts[Field.LIQUIDITY_PERCENT]?.equalTo(new Percent('1'))
  // pair contract
  const pairContract: Contract | null = usePairContract(pair?.liquidityToken?.address)
  // allowance handling
  const [signatureData, setSignatureData] = useState<{ v: number; r: string; s: string; deadline: number } | null>(null)
  const [approval, approveCallback] = useApproveCallback(parsedAmounts[Field.LIQUIDITY], V2_ROUTER_ADDRESSES[chainId])

  async function onAttemptToApprove() {
    if (!pairContract || !pair || !library || !deadline) throw new Error('missing dependencies')
    const liquidityAmount = parsedAmounts[Field.LIQUIDITY]
    if (!liquidityAmount) {
      toastError(t('Error'), t('Missing liquidity amount'))
      throw new Error('missing liquidity amount')
    }

    // try to gather a signature for permission
    const nonce = await pairContract.nonces(account)
    const EIP712Domain = [
      { name: 'name', type: 'string' },
      { name: 'version', type: 'string' },
      { name: 'chainId', type: 'uint256' },
      { name: 'verifyingContract', type: 'address' },
    ]
    const domain = { name: 'BaseSwap LPs', version: '1', chainId, verifyingContract: pair.liquidityToken.address }
    const Permit = [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'nonce', type: 'uint256' },
      { name: 'deadline', type: 'uint256' },
    ]
    const message = {
      owner: account,
      spender: V2_ROUTER_ADDRESSES[chainId],
      value: liquidityAmount.raw.toString(),
      nonce: nonce.toHexString(),
      deadline: deadline.toNumber(),
    }
    const data = JSON.stringify({ types: { EIP712Domain, Permit }, domain, primaryType: 'Permit', message })
    library
      .send('eth_signTypedData_v4', [account, data])
      .then(splitSignature)
      .then((signature) => {
        setSignatureData({ v: signature.v, r: signature.r, s: signature.s, deadline: deadline.toNumber() })
      })
      .catch((err) => {
        // for all errors other than 4001 (EIP-1193 user rejected request), fall back to manual approve
        if (err?.code !== 4001) {
          approveCallback()
        }
      })
  }

  // wrapped onUserInput to clear signatures
  const onUserInput = useCallback(
    (field: Field, value: string) => {
      setSignatureData(null)
      return _onUserInput(field, value)
    },
    [_onUserInput],
  )

  const onLiquidityInput = useCallback((value: string): void => onUserInput(Field.LIQUIDITY, value), [onUserInput])
  const onCurrencyAInput = useCallback((value: string): void => onUserInput(Field.CURRENCY_A, value), [onUserInput])
  const onCurrencyBInput = useCallback((value: string): void => onUserInput(Field.CURRENCY_B, value), [onUserInput])
  // tx sending
  const addTransaction = useTransactionAdder()

  async function onRemove() {
    if (!chainId || !library || !account || !deadline) throw new Error('missing dependencies')
    const { [Field.CURRENCY_A]: currencyAmountA, [Field.CURRENCY_B]: currencyAmountB } = parsedAmounts
    if (!currencyAmountA || !currencyAmountB) {
      toastError(t('Error'), t('Missing currency amounts'))
      throw new Error('missing currency amounts')
    }
    const amountsMin = {
      [Field.CURRENCY_A]: calculateSlippageAmount(currencyAmountA, allowedSlippage)[0],
      [Field.CURRENCY_B]: calculateSlippageAmount(currencyAmountB, allowedSlippage)[0],
    }
    if (!currencyA || !currencyB) {
      toastError(t('Error'), t('Missing tokens'))
      throw new Error('missing tokens')
    }
    const liquidityAmount = parsedAmounts[Field.LIQUIDITY]
    if (!liquidityAmount) {
      toastError(t('Error'), t('Missing liquidity amount'))
      throw new Error('missing liquidity amount')
    }
    const currencyBIsBNB = currencyB === ETHER
    const oneCurrencyIsBNB = currencyA === ETHER || currencyBIsBNB
    if (!tokenA || !tokenB) {
      toastError(t('Error'), t('Could not wrap'))
      throw new Error('could not wrap')
    }
    let methodNames: string[]
    let args: Array<string | string[] | number | boolean>
    // we have approval, use normal remove liquidity
    if (approval === ApprovalState.APPROVED) {
      // removeLiquidityETH
      if (oneCurrencyIsBNB) {
        methodNames = ['removeLiquidityETH', 'removeLiquidityETHSupportingFeeOnTransferTokens']
        args = [
          currencyBIsBNB ? tokenA.address : tokenB.address,
          liquidityAmount.raw.toString(),
          amountsMin[currencyBIsBNB ? Field.CURRENCY_A : Field.CURRENCY_B].toString(),
          amountsMin[currencyBIsBNB ? Field.CURRENCY_B : Field.CURRENCY_A].toString(),
          account,
          deadline.toHexString(),
        ]
      }
      // removeLiquidity
      else {
        methodNames = ['removeLiquidity']
        args = [
          tokenA.address,
          tokenB.address,
          liquidityAmount.raw.toString(),
          amountsMin[Field.CURRENCY_A].toString(),
          amountsMin[Field.CURRENCY_B].toString(),
          account,
          deadline.toHexString(),
        ]
      }
    }
    // we have a signature, use permit versions of remove liquidity
    else if (signatureData !== null) {
      // removeLiquidityETHWithPermit
      if (oneCurrencyIsBNB) {
        methodNames = ['removeLiquidityETHWithPermit', 'removeLiquidityETHWithPermitSupportingFeeOnTransferTokens']
        args = [
          currencyBIsBNB ? tokenA.address : tokenB.address,
          liquidityAmount.raw.toString(),
          amountsMin[currencyBIsBNB ? Field.CURRENCY_A : Field.CURRENCY_B].toString(),
          amountsMin[currencyBIsBNB ? Field.CURRENCY_B : Field.CURRENCY_A].toString(),
          account,
          signatureData.deadline,
          false,
          signatureData.v,
          signatureData.r,
          signatureData.s,
        ]
      }
      // removeLiquidityETHWithPermit
      else {
        methodNames = ['removeLiquidityWithPermit']
        args = [
          tokenA.address,
          tokenB.address,
          liquidityAmount.raw.toString(),
          amountsMin[Field.CURRENCY_A].toString(),
          amountsMin[Field.CURRENCY_B].toString(),
          account,
          signatureData.deadline,
          false,
          signatureData.v,
          signatureData.r,
          signatureData.s,
        ]
      }
    } else {
      toastError(t('Error'), t('Attempting to confirm without approval or a signature'))
      throw new Error('Attempting to confirm without approval or a signature')
    }

    const routerContract = getRouterContract(chainId, library, account)

    const safeGasEstimates: (BigNumber | undefined)[] = await Promise.all(
      methodNames.map((methodName) =>
        routerContract.estimateGas[methodName](...args)
          .then(calculateGasMargin)
          .catch((err) => {
            console.error(`estimateGas failed`, methodName, args, err)
            return undefined
          }),
      ),
    )

    const indexOfSuccessfulEstimation = safeGasEstimates.findIndex((safeGasEstimate) =>
      BigNumber.isBigNumber(safeGasEstimate),
    )

    // all estimations failed...
    if (indexOfSuccessfulEstimation === -1) {
      toastError(t('Error'), t('This transaction would fail'))
    } else {
      const methodName = methodNames[indexOfSuccessfulEstimation]
      const safeGasEstimate = safeGasEstimates[indexOfSuccessfulEstimation]

      setLiquidityState({ attemptingTxn: true, liquidityErrorMessage: undefined, txHash: undefined })
      await routerContract[methodName](...args, {
        gasLimit: safeGasEstimate,
        gasPrice,
      })
        .then((response: TransactionResponse) => {
          setLiquidityState({ attemptingTxn: false, liquidityErrorMessage: undefined, txHash: response.hash })
          addTransaction(response, {
            summary: `Remove ${parsedAmounts[Field.CURRENCY_A]?.toSignificant(3)} ${
              currencyA?.symbol
            } and ${parsedAmounts[Field.CURRENCY_B]?.toSignificant(3)} ${currencyB?.symbol}`,
            type: 'remove-liquidity',
          })
        })
        .catch((err) => {
          if (err && err.code !== 4001) {
            logError(err)
            console.error(`Remove Liquidity failed`, err, args)
          }
          setLiquidityState({
            attemptingTxn: false,
            liquidityErrorMessage:
              err && err?.code !== 4001
                ? t('Remove liquidity failed: %message%', { message: transactionErrorToUserReadableMessage(err, t) })
                : undefined,
            txHash: undefined,
          })
        })
    }
  }

  const liquidityPercentChangeCallback = useCallback(
    (value: number) => {
      onUserInput(Field.LIQUIDITY_PERCENT, value.toString())
    },
    [onUserInput],
  )

  const oneCurrencyIsBNB = currencyA === ETHER || currencyB === ETHER
  const oneCurrencyIsWBNB = Boolean(
    chainId &&
      ((currencyA && currencyEquals(WNATIVE[chainId], currencyA)) ||
        (currencyB && currencyEquals(WNATIVE[chainId], currencyB))),
  )
  const handleDismissConfirmation = useCallback(() => {
    setSignatureData(null) // important that we clear signature data to avoid bad sigs
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.LIQUIDITY_PERCENT, '0')
    }
  }, [onUserInput, txHash])

  const [innerLiquidityPercentage, setInnerLiquidityPercentage] = useDebouncedChangeHandler(
    Number.parseInt(parsedAmounts[Field.LIQUIDITY_PERCENT].toFixed(0)),
    liquidityPercentChangeCallback,
  )
  // console.log(innerLiquidityPercentage)

  const handleChangePercent = useCallback(
    (value) => setInnerLiquidityPercentage(Math.ceil(value)),
    [setInnerLiquidityPercentage],
  )

  const isZap = false
  const isZapOutA = isZap && !removalCheckedB && removalCheckedA
  const isZapOutB = isZap && !removalCheckedA && removalCheckedB
  const { isMobile, isDesktop } = useMatchBreakpointsContext()

  return (
    <Flex width="100%" height="100%" flexDirection="column" marginTop={isDesktop ? '1rem' : '4px'}>
      <Flex
        flexDirection={isDesktop ? 'row' : 'column'}
        alignItems="center"
        padding="0px"
        justifyContent="space-between"
        width="100%"
      >
        <Flex alignItems="center" marginBottom={isDesktop ? '0px' : '4px'}>
          <CurrencyLogo style={{ margin: '4px' }} currency={currencyA} size={isMobile ? '40px' : '50px'} />
          <CurrencyLogo style={{ margin: '4px' }} currency={currencyB} size={isMobile ? '40px' : '50px'} />
          <Text fontSize="24px">
            {currencyA.symbol}-{currencyB.symbol} LP
          </Text>
        </Flex>

        <Flex
          flexDirection={isDesktop ? 'column' : 'row'}
          alignItems={isDesktop ? 'flex-end' : 'center'}
          justifyContent={isDesktop ? 'flex-end' : 'space-evenly'}
        >
          <Flex>
            <Flex flexDirection="row" alignItems="center" mr="4px">
              <CurrencyLogo currency={currencyA} size={isMobile ? '20px' : '20px'} />
              {tokenADeposited?.toSignificant(3)} {currencyA.symbol}
            </Flex>
            <Flex flexDirection="row" alignItems="center" mr="4px">
              <CurrencyLogo currency={currencyB} size={isMobile ? '20px' : '20px'} />
              {tokenBDeposited?.toSignificant(3)} {currencyB.symbol}
            </Flex>
          </Flex>
          <Text marginRight="8px">
            ${totalUSDValue?.toFixed(2) || 0}
            {/* {formatToFourDecimalPlaces(userPoolBalance?.toSignificant(3))} LP SHARES     */}
          </Text>
        </Flex>
      </Flex>
      <Flex flexDirection="column" justifyContent="center">
        <Text mt="8px" textAlign="center" fontSize="20px">
          {t('REMOVE:')}
        </Text>
        <BorderCard>
          <Text fontSize="40px" bold mb="0px" style={{ lineHeight: 1 }}>
            {formattedAmounts[Field.LIQUIDITY_PERCENT]}%
          </Text>
          <Slider min={0} max={100} value={innerLiquidityPercentage} onChange={handleChangePercent} step={1} />
          <Flex flexWrap="wrap" alignItems="flex-start" justifyContent="space-evenly">
            <Button variant="percentages" scale="sm" onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, '25')}>
              {' '}
              25%{' '}
            </Button>
            <Button variant="percentages" scale="sm" onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, '50')}>
              {' '}
              50%{' '}
            </Button>
            <Button variant="percentages" scale="sm" onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, '75')}>
              {' '}
              75%{' '}
            </Button>
            <Button variant="percentages" scale="sm" onClick={() => onUserInput(Field.LIQUIDITY_PERCENT, '100')}>
              {' '}
              {t('MAX')}{' '}
            </Button>
          </Flex>
        </BorderCard>
        <>
          <Flex flexDirection="column" justifyContent="center" alignItems="center" marginTop="8px">
            <Text color="text" fontSize="20px" textTransform="uppercase">
              {t('Receive:')}
            </Text>
            <LightGreyCard>
              <Flex justifyContent="space-between" mb="4px" as="label" alignItems="center">
                <Flex alignItems="center">
                  <CurrencyLogo currency={currencyA} />
                  <Text small color="textSubtle" id="remove-liquidity-tokena-symbol" ml="4px">
                    {currencyA?.symbol}
                  </Text>
                </Flex>
                <Flex>
                  <Text small bold>
                    {formattedAmounts[Field.CURRENCY_A] || '0'}
                  </Text>
                  <Text small ml="4px">
                    ({isZapOutA ? '100' : !isZap ? '50' : '0'}%)
                  </Text>
                </Flex>
              </Flex>
              <Flex justifyContent="space-between" as="label" alignItems="center">
                <Flex alignItems="center">
                  <CurrencyLogo currency={currencyB} />
                  <Text small color="textSubtle" id="remove-liquidity-tokenb-symbol" ml="4px">
                    {currencyB?.symbol}
                  </Text>
                </Flex>
                <Flex>
                  <Text bold small>
                    {formattedAmounts[Field.CURRENCY_B] || '0'}
                  </Text>
                  <Text small ml="4px">
                    ({isZapOutB ? '100' : !isZap ? '50' : '0'}%)
                  </Text>
                </Flex>
              </Flex>
              {chainId && (oneCurrencyIsWBNB || oneCurrencyIsBNB) ? (
                <RowBetween style={{ justifyContent: 'flex-end', fontSize: '14px' }}>
                  {oneCurrencyIsBNB ? (
                    <StyledInternalLink
                      href={`/remove/${currencyA === ETHER ? WNATIVE[chainId].address : currencyIdA}/${
                        currencyB === ETHER ? WNATIVE[chainId].address : currencyIdB
                      }`}
                    >
                      {t('Receive WETH')}
                    </StyledInternalLink>
                  ) : oneCurrencyIsWBNB ? (
                    <StyledInternalLink
                      href={`/remove/${
                        currencyA && currencyEquals(currencyA, WNATIVE[chainId]) ? 'ETH' : currencyIdA
                      }/${currencyB && currencyEquals(currencyB, WNATIVE[chainId]) ? 'ETH' : currencyIdB}`}
                    >
                      {t('Receive ETH')}
                    </StyledInternalLink>
                  ) : null}
                </RowBetween>
              ) : null}
            </LightGreyCard>
          </Flex>
        </>

        {pair && (
          <RowBetween mt="4px">
            <Text color="text" fontSize={isDesktop ? '12px' : '14px'}>
              {' '}
              {t('Prices: ')}{' '}
            </Text>
            <Flex flexDirection="column" alignItems="flex-end">
              <Text color="text" fontSize="14px">
                1 {currencyA?.symbol} = {tokenA ? pair.priceOf(tokenA).toSignificant(6) : '-'} {currencyB?.symbol}
              </Text>
              <Text small color="textSubtle">
                1 {currencyB?.symbol} = {tokenB ? pair.priceOf(tokenB).toSignificant(6) : '-'} {currencyA?.symbol}
              </Text>
            </Flex>
          </RowBetween>
        )}

        <RowBetween mt="4px">
          <Text color="text" fontSize={isDesktop ? '12px' : '14px'}>
            {t('Slippage: ')}
          </Text>
          <Flex justifyContent="center" alignItems="center">
            <Text color="text" fontSize="14px">
              {allowedSlippage / 100}%
            </Text>
            <GlobalSettings mode={SettingsMode.SWAP_LIQUIDITY} size="18px" />
          </Flex>
        </RowBetween>

        <Box position="relative" mt="16px">
          {!account ? (
            <ConnectWalletButton width="100%" />
          ) : (
            <RowBetween>
              <Button
                variant={
                  approval === ApprovalState.APPROVED || (!isZap && signatureData !== null)
                    ? 'success'
                    : 'secondarytrans'
                }
                onClick={isZap ? approveCallback : onAttemptToApprove}
                disabled={approval !== ApprovalState.NOT_APPROVED || (!isZap && signatureData !== null)}
                width="100%"
                marginRight="0.5rem"
              >
                {approval === ApprovalState.PENDING ? (
                  <RingLoader
                    color="#37C0DF"
                    // loading={loading}
                    // cssOverride={override}
                    size={25}
                    aria-setsize={25}
                    speedMultiplier={0.9}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                ) : approval === ApprovalState.APPROVED || (!isZap && signatureData !== null) ? (
                  t('ENABLED')
                ) : (
                  t('ENABLE')
                )}
              </Button>
              <Button
                variant={
                  !isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]
                    ? 'danger'
                    : 'primary'
                }
                onClick={() => {
                  setLiquidityState({
                    attemptingTxn: false,
                    liquidityErrorMessage: undefined,
                    txHash: undefined,
                  })
                  onRemove()
                }}
                width="100%"
                disabled={
                  !isValid ||
                  (!isZap && signatureData === null && approval !== ApprovalState.APPROVED) ||
                  (isZap && approval !== ApprovalState.APPROVED)
                }
              >
                {error || t('Remove')}
                {/* should add pacman loader while the transaction is pending in wallet  */}
              </Button>
            </RowBetween>
          )}
        </Box>
      </Flex>
    </Flex>
  )
}
